import { useEffect, useMemo, useState } from 'react';
import type { POI, Schedule } from '@poi-planner/shared';
import { useLibraryStore } from '../store/libraryStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useUIStore } from '../store/uiStore';
import { useTripStore } from '../store/tripStore';
import {
  usePoiFilters,
  type ActiveFilterCounts,
  type MapBounds,
  type SortMode,
  type TagFilterMode,
  type TagWithCount,
} from './usePoiFilters';

export interface TripSidebarController {
  hideScheduled: boolean;
  setHideScheduled: (value: boolean) => void;
  focusModeEnabled: boolean;
  setFocusModeEnabled: (value: boolean) => void;
  targetPoiId: string | null;
  setTargetPoiId: (poiId: string | null) => void;
  targetPoi: POI | null;
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagFilters: Map<string, TagFilterMode>;
  sourceFilters: Map<string, TagFilterMode>;
  collectionFilters: Map<string, TagFilterMode>;
  allTags: TagWithCount[];
  filteredPOIs: POI[];
  distanceMap: Map<string, number>;
  allSourceTexts: string[];
  activeFilterCounts: ActiveFilterCounts;
  collectionOptions: { id: string; name: string }[];
  scheduledCounts: Map<string, number>;
  hasScheduledCoords: boolean;
  // Trip filter
  tripFilterEnabled: boolean;
  setTripFilterEnabled: (value: boolean) => void;
  tripPoiIds: Set<string>;
  // Pin actions
  pinnedPoiIds: Set<string>;
  onTogglePin: (poiId: string) => void;
  setSearch: (value: string) => void;
  setSortMode: (mode: SortMode) => void;
  setSortAscending: (ascending: boolean) => void;
  toggleTag: (tag: string) => void;
  toggleSource: (source: string) => void;
  toggleCollection: (collectionId: string) => void;
  clearAllFilters: () => void;
  totalPoiCount: number;
}

interface UseTripSidebarControllerOptions {
  boundsFilterEnabled?: boolean;
  mapBounds?: MapBounds | null;
}

export function useTripSidebarController(
  pois: POI[],
  schedule: Schedule,
  options: UseTripSidebarControllerOptions = {}
): TripSidebarController {
  const { boundsFilterEnabled = false, mapBounds = null } = options;
  const [hideScheduled, setHideScheduled] = useState(false);
  const [tripFilterEnabled, setTripFilterEnabled] = useState(false);
  const [prevSortMode, setPrevSortMode] = useState<Exclude<SortMode, 'distance'>>('alpha');

  const targetPoiId = useUIStore((s) => s.targetPoiId);
  const setTargetPoiId = useUIStore((s) => s.setTargetPoiId);
  const focusModeEnabled = useUIStore((s) => s.focusModeEnabled);
  const setFocusModeEnabled = useUIStore((s) => s.setFocusModeEnabled);
  const getPOI = useLibraryStore((s) => s.getPOI);
  const getAccommodation = useLibraryStore((s) => s.getAccommodation);
  const collections = useCollectionsStore((s) => s.collections);
  const pinnedPoiIds = useTripStore((s) => s.pinnedPoiIds);
  const pinPoi = useTripStore((s) => s.pinPoi);
  const unpinPoi = useTripStore((s) => s.unpinPoi);

  const collectionPoiMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const collection of collections) {
      map.set(collection.id, new Set(collection.poiIds));
    }
    return map;
  }, [collections]);

  const collectionOptions = useMemo(() => {
    return collections.map((c) => ({ id: c.id, name: c.name }));
  }, [collections]);

  const scheduledCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const day of schedule.days) {
      for (const sp of day.scheduledPOIs) {
        counts.set(sp.poiId, (counts.get(sp.poiId) ?? 0) + 1);
      }
    }
    return counts;
  }, [schedule]);

  // tripPoiIds = pinned union scheduled
  const tripPoiIds = useMemo(() => {
    const ids = new Set<string>(pinnedPoiIds);
    for (const id of scheduledCounts.keys()) {
      ids.add(id);
    }
    return ids;
  }, [pinnedPoiIds, scheduledCounts]);


  const scheduledCoords = useMemo(() => {
    const coords: { lat: number; lng: number }[] = [];
    for (const day of schedule.days) {
      for (const sp of day.scheduledPOIs) {
        const poi = getPOI(sp.poiId);
        if (poi?.lat != null && poi?.lng != null) {
          coords.push({ lat: poi.lat, lng: poi.lng });
        }
      }
      if (day.sleep) {
        const accom = getAccommodation(day.sleep.poiId);
        if (accom?.lat != null && accom?.lng != null) {
          coords.push({ lat: accom.lat, lng: accom.lng });
        }
      }
    }
    return coords;
  }, [schedule, getPOI, getAccommodation]);

  const targetPoi = useMemo(() => {
    if (!targetPoiId) return null;
    return pois.find((p) => p.id === targetPoiId) ?? null;
  }, [pois, targetPoiId]);

  const {
    search,
    sortMode,
    sortAscending,
    tagFilters,
    sourceFilters,
    collectionFilters,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    activeFilterCounts,
    setSearch,
    setSortMode,
    setSortAscending,
    toggleTag,
    clearTags,
    toggleSource,
    clearSources,
    toggleCollection,
    clearCollections,
  } = usePoiFilters(pois, {
    hideScheduled: {
      enabled: hideScheduled,
      scheduledCounts,
    },
    focusMode: {
      enabled: focusModeEnabled,
      scheduledCoords,
    },
    distanceTarget: {
      targetPoiId,
      allPois: pois,
    },
    collectionPoiMap,
    boundsFilter: {
      enabled: boundsFilterEnabled,
      bounds: mapBounds,
    },
    tripFilter: {
      enabled: tripFilterEnabled,
      poiIds: tripPoiIds,
    },
  });

  useEffect(() => {
    if (targetPoiId) {
      if (sortMode !== 'distance') {
        setPrevSortMode(sortMode as Exclude<SortMode, 'distance'>);
        setSortMode('distance');
      }
    } else if (sortMode === 'distance') {
      setSortMode(prevSortMode);
    }
  }, [targetPoiId, sortMode, prevSortMode, setSortMode]);

  const onTogglePin = (poiId: string) => {
    if (pinnedPoiIds.has(poiId)) {
      unpinPoi(poiId);
    } else {
      pinPoi(poiId);
    }
  };

  return {
    hideScheduled,
    setHideScheduled,
    focusModeEnabled,
    setFocusModeEnabled,
    targetPoiId,
    setTargetPoiId,
    targetPoi,
    search,
    sortMode,
    sortAscending,
    tagFilters,
    sourceFilters,
    collectionFilters,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    activeFilterCounts,
    collectionOptions,
    scheduledCounts,
    hasScheduledCoords: scheduledCoords.length > 0,
    tripFilterEnabled,
    setTripFilterEnabled,
    tripPoiIds,
    pinnedPoiIds,
    onTogglePin,
    setSearch,
    setSortMode,
    setSortAscending,
    toggleTag,
    toggleSource,
    toggleCollection,
    clearAllFilters: () => {
      setSearch('');
      clearTags();
      clearSources();
      clearCollections();
    },
    totalPoiCount: pois.length,
  };
}
